// src/services/swmsService.ts
// Fetches live SWMS records from the Cloudflare Worker API (Neon database).
// Maps to the shape SWMS.tsx expects for the list view.
//
// Rich fields (hazards, highRiskActivities, signons, scopeOfWork) are not
// yet stored in the API — they default to empty arrays. The detail view
// will show available Neon data; mock SWMS records retain full rich detail.

import { api } from '../utils/api'

interface ApiSWMS {
  id:          string
  jobId?:      string
  job_id?:     string
  title?:      string
  revision?:   string | null
  status?:     string
  approvedAt?: string | null
  approved_at?: string | null
  createdAt?:  string
  created_at?: string
}

export interface MappedSWMS {
  id:                  string
  swmsNumber:          string   // synthesised from id suffix — no sequence in schema yet
  title:               string
  jobId:               string
  jobNumber:           string   // enriched from jobs join in a future step
  revision:            string
  status:              string
  approvedAt:          string | null
  createdAt:           string
  // Rich fields stored in Neon as JSONB — not yet in the list endpoint
  highRiskActivities:  string[]
  hazards:             never[]
  signons:             never[]
}

function mapApiSWMS(raw: ApiSWMS): MappedSWMS {
  return {
    id:                 raw.id,
    swmsNumber:         `SWMS-${raw.id.slice(-6).toUpperCase()}`,
    title:              raw.title ?? 'Untitled SWMS',
    jobId:              raw.jobId ?? raw.job_id ?? '',
    jobNumber:          '',        // enriched from jobs join — Phase 6 step
    revision:           raw.revision ?? '1',
    status:             raw.status  ?? 'DRAFT',
    approvedAt:         raw.approvedAt ?? raw.approved_at ?? null,
    createdAt:          raw.createdAt  ?? raw.created_at  ?? new Date().toISOString(),
    highRiskActivities: [],
    hazards:            [],
    signons:            [],
  }
}

export async function fetchSWMS(jobId?: string): Promise<MappedSWMS[]> {
  const params = jobId ? `?jobId=${jobId}` : ''
  const { data } = await api.get<{ data: ApiSWMS[] } | ApiSWMS[]>(`/api/whs/swms${params}`)
  const raw: ApiSWMS[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiSWMS)
}
